/**
 * GreenPageLayout — shared chrome for all participant-facing screens.
 *
 * Provides the full dark-green (#216732) viewport background with biomorphic
 * photo blobs and colour shapes drifting upward at the edges, and a single
 * large stone (#fff8eb) rounded card centred on screen that holds all page
 * content. The pebble background fades out smoothly (2s) when showPebbles
 * transitions to false.
 */

import { useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import { ScrollingPebbleBackground } from './ScrollingPebbleBackground'
import type { GroundingImage } from './ScrollingPebbleBackground'

export interface GreenPageLayoutProps {
  /** Page content — rendered inside the centred stone card */
  children: ReactNode
  /** Optional community images for photo pebbles at edges */
  images?: GroundingImage[]
  /** Show the biomorphic pebble background. Pass false to fade out then unmount. */
  showPebbles?: boolean
  /** Additional Tailwind classes for the stone card */
  className?: string
}

export function GreenPageLayout({ children, images = [], showPebbles = true, className = '' }: GreenPageLayoutProps) {
  // Two-phase visibility: `visible` controls opacity, `mounted` controls DOM presence
  const [mounted, setMounted] = useState(showPebbles)
  const [visible, setVisible] = useState(showPebbles)

  useEffect(() => {
    let rafId: number
    let timerId: ReturnType<typeof setTimeout>
    if (showPebbles) {
      setMounted(true)
      // Small delay so the mount happens before the opacity transition starts
      rafId = requestAnimationFrame(() => setVisible(true))
    } else {
      setVisible(false)
      // Safety net: unmount after transition duration + buffer, in case
      // transitionend doesn't fire (e.g. background tab throttling, reduced motion)
      timerId = setTimeout(() => setMounted(false), 2500)
    }
    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timerId)
    }
  }, [showPebbles])

  const handleTransitionEnd = useCallback((e: React.TransitionEvent<HTMLDivElement>) => {
    // Only react to this element's own transition, not bubbled child transitions
    if (e.target === e.currentTarget && !visible) setMounted(false)
  }, [visible])

  return (
    <div className="h-screen bg-dark-green flex flex-col relative overflow-hidden p-4 sm:p-6">
      {/* Drifting photo circles and colour blobs — fades out over 2s */}
      {mounted && (
        <div
          className="absolute inset-0 z-0"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 2s ease-out',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          <ScrollingPebbleBackground images={images} />
        </div>
      )}

      {/* Single centred stone card — all participant content lives here */}
      <div
        data-testid="stone-card"
        className={`relative z-10 flex-1 min-h-0 w-full max-w-4xl mx-auto bg-stone rounded-[40px] overflow-hidden flex flex-col ${className}`}
      >
        {children}
      </div>

    </div>
  )
}
